import React, { useMemo, useState } from "react";
import { Player } from "@remotion/player";
import { Alert, Button, Input, Modal, Spin, Tooltip, Typography } from "antd";

import {
  CLIP_FPS,
  ClipPreview,
  captionAnchorForFraming,
  DEFAULT_CLIP_BRAND,
  planClipCaptions,
  splitIntoSentences,
  toCaptionedClipStyle,
  toCaptionPagingOptions,
  type ClipCaptionPlan,
  type ClipWord,
} from "@fitness-app/ai/studio-editor";

import { useRerenderClip, useStudioClipEditorData } from "../hooks/useClipEditor";
import { type StudioClipRow } from "../hooks/useStudioData";

const PREVIEW_WIDTH = 270;
const PREVIEW_HEIGHT = 480;

const formatMs = (ms: number) => {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, "0")}`;
};

interface ClipEditorModalProps {
  clip: StudioClipRow;
  onClose: () => void;
}

/**
 * Edytor decyzji montażowej (TRE-1975, etap F1): przycięcie klipu na granicach zdań i
 * poprawki słów w napisach. Podgląd gra z nagrania źródłowego tą samą logiką planu co
 * render, a „Renderuj ponownie" zapisuje decyzję jako nową rewizję i uruchamia task —
 * plik zawsze powstaje po stronie serwera (Bunny, QC, brand).
 */
export const ClipEditorModal = ({ clip, onClose }: ClipEditorModalProps): React.ReactElement => {
  const editorQuery = useStudioClipEditorData(clip);
  const rerender = useRerenderClip();
  const initialWindow = clip.edit ?? { startMs: clip.startMs, endMs: clip.endMs };
  const [startMs, setStartMs] = useState(initialWindow.startMs);
  const [endMs, setEndMs] = useState(initialWindow.endMs);
  const [captionEdits, setCaptionEdits] = useState<Map<number, string>>(
    () => new Map((clip.edit?.captionEdits ?? []).map((edit) => [edit.wordStartMs, edit.text])),
  );
  const [editingWordMs, setEditingWordMs] = useState<number | null>(null);

  const words = editorQuery.data?.words ?? [];
  const sentences = useMemo(() => splitIntoSentences(words), [words]);
  const edits = useMemo(() => [...captionEdits].map(([wordStartMs, text]) => ({ wordStartMs, text })), [captionEdits]);

  const plan = useMemo<{ plan: ClipCaptionPlan | null; error: string | null }>(() => {
    if (!words.length) return { plan: null, error: null };
    try {
      return {
        plan: planClipCaptions(words, startMs, endMs, {
          ...toCaptionPagingOptions(DEFAULT_CLIP_BRAND),
          captionEdits: edits,
        }),
        error: null,
      };
    } catch {
      // snapToSentences: no sentence boundary inside the chosen window.
      return { plan: null, error: "W wybranym oknie nie ma pełnego zdania. Przesuń Początek albo Koniec." };
    }
  }, [words, startMs, endMs, edits]);

  const setWordText = (word: ClipWord, text: string) => {
    setCaptionEdits((current) => {
      const next = new Map(current);
      if (text.trim() === word.text) next.delete(word.startMs);
      else next.set(word.startMs, text.trim());
      return next;
    });
  };

  const durationMs = plan.plan?.durationMs ?? 0;
  const style = toCaptionedClipStyle(DEFAULT_CLIP_BRAND, captionAnchorForFraming(clip.renderMeta?.framing?.mode));
  const durationInFrames = Math.max(1, Math.ceil((durationMs / 1000) * CLIP_FPS));

  return (
    <Modal
      open
      width="min(1040px, 96vw)"
      title={`Edycja klipu: ${clip.hook.slice(0, 80)}`}
      onCancel={onClose}
      footer={
        <div className="flex items-center justify-between">
          <Typography.Text type="secondary">
            Długość: {formatMs(durationMs)} · rewizja {clip.revision}
          </Typography.Text>
          <div className="flex gap-2">
            <Button onClick={onClose}>Anuluj</Button>
            <Tooltip title="Zapisuje przycięcie i poprawki napisów, potem renderuje klip od nowa (ok. 1–2 min).">
              <Button
                type="primary"
                loading={rerender.isLoading}
                disabled={!plan.plan || !editorQuery.data}
                onClick={() =>
                  rerender.mutate({ clipId: clip.id, startMs, endMs, captionEdits: edits }, { onSuccess: onClose })
                }
              >
                Renderuj ponownie
              </Button>
            </Tooltip>
          </div>
        </div>
      }
    >
      {editorQuery.isLoading && (
        <div className="flex justify-center py-12">
          <Spin />
        </div>
      )}
      {editorQuery.isError && <Alert type="error" showIcon message="Nie udało się wczytać transkrypcji tego klipu." />}
      {editorQuery.data && (
        <div className="flex gap-6">
          <div className="shrink-0" style={{ width: PREVIEW_WIDTH }}>
            {plan.plan ? (
              <Player
                component={ClipPreview}
                inputProps={{
                  src: editorQuery.data.sourceUrl,
                  segments: plan.plan.segments,
                  events: plan.plan.events,
                  style,
                  framing: clip.renderMeta?.framing ?? null,
                  logoUrl: DEFAULT_CLIP_BRAND.logoUrl,
                }}
                durationInFrames={durationInFrames}
                fps={CLIP_FPS}
                compositionWidth={DEFAULT_CLIP_BRAND.targetWidth}
                compositionHeight={DEFAULT_CLIP_BRAND.targetHeight}
                style={{ width: PREVIEW_WIDTH, height: PREVIEW_HEIGHT, borderRadius: 8, overflow: "hidden" }}
                controls
                loop
              />
            ) : (
              <div
                className="flex items-center justify-center rounded bg-gray-100 p-4 text-center text-sm text-gray-500"
                style={{ width: PREVIEW_WIDTH, height: PREVIEW_HEIGHT }}
              >
                {plan.error ?? "Brak podglądu"}
              </div>
            )}
            <Typography.Paragraph type="secondary" className="!mb-0 !mt-2 text-xs">
              Podgląd gra z nagrania źródłowego. Cisza między zdaniami jest wycinana tak samo jak w renderze.
              {clip.renderMeta?.framing && clip.renderMeta.framing.mode !== "pad"
                ? " Kadr z ostatniego renderu: zdania poza nim pokażą się z pasami, do czasu ponownego renderu."
                : ""}
            </Typography.Paragraph>
          </div>

          <div className="min-w-0 flex-1">
            <Typography.Paragraph type="secondary" className="!mb-2 text-xs">
              „Początek" ustawia pierwsze zdanie klipu, „Koniec" ostatnie. Kliknij słowo, żeby poprawić napis (audio
              zostaje bez zmian).
            </Typography.Paragraph>
            <div className="max-h-[480px] overflow-y-auto pr-2">
              {sentences.map((sentence) => {
                const first = sentence[0]!;
                const last = sentence[sentence.length - 1]!;
                const inside = first.startMs >= startMs && last.endMs <= endMs;
                return (
                  <div
                    key={first.startMs}
                    className={`mb-1 flex items-start gap-2 rounded px-2 py-1 ${inside ? "bg-yellow-50" : "opacity-60"}`}
                  >
                    <Button
                      size="small"
                      type={first.startMs === startMs ? "primary" : "default"}
                      disabled={first.startMs >= endMs}
                      onClick={() => setStartMs(first.startMs)}
                    >
                      Początek
                    </Button>
                    <div className="flex min-w-0 flex-1 flex-wrap gap-x-1 leading-7">
                      {sentence.map((word) => {
                        const text = captionEdits.get(word.startMs) ?? word.text;
                        if (editingWordMs === word.startMs) {
                          return (
                            <Input
                              key={word.startMs}
                              size="small"
                              autoFocus
                              defaultValue={text}
                              style={{ width: Math.max(64, text.length * 10 + 24) }}
                              onBlur={(event) => {
                                setWordText(word, event.target.value);
                                setEditingWordMs(null);
                              }}
                              onPressEnter={(event) => {
                                setWordText(word, event.currentTarget.value);
                                setEditingWordMs(null);
                              }}
                            />
                          );
                        }
                        return (
                          <span
                            key={word.startMs}
                            role="button"
                            tabIndex={0}
                            title="Popraw słowo w napisach"
                            className={`cursor-text rounded px-0.5 hover:bg-blue-50 ${
                              captionEdits.has(word.startMs) ? "font-semibold text-blue-600" : ""
                            }`}
                            onClick={() => setEditingWordMs(word.startMs)}
                            onKeyDown={(event) => event.key === "Enter" && setEditingWordMs(word.startMs)}
                          >
                            {text}
                          </span>
                        );
                      })}
                    </div>
                    <Button
                      size="small"
                      type={last.endMs === endMs ? "primary" : "default"}
                      disabled={last.endMs <= startMs}
                      onClick={() => setEndMs(last.endMs)}
                    >
                      Koniec
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};
